import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import PageContainer from '../PageContainer/PageContainer';
import AuthMessage from '../AuthMessage/AuthMessage';
import reservationService, { ReservationRequest } from '../../services/reservationService';
import restaurantService, { PlanFeaturesPayload } from '../../services/restaurantService';
import { CUSTOMER_FEATURE_TABLE_BOOKING, CUSTOMER_FEATURE_VIRTUAL_CAPTAIN, hasCustomerFeature } from '../../utils/planFeatures';
import { trackEvent } from '../../utils/analytics';
import { useAppSelector } from '../../store/hooks';
import { IconCalendarSmall } from '../RestaurantHome/RestaurantDetailIcons';
import PeppyReservationAssistant from './PeppyReservationAssistant';
import {
  datetimeLocalValueToUtcIso,
  formatReservationDateTime,
  getUserTimeZone,
  getUserTimeZoneLabel,
  utcIsoToDatetimeLocalValue,
} from '../../utils/reservationDateTime';
import './TableReservation.css';

type LocationOption = { id: number; name: string };

const occasions = [
  { id: 'Dine-In', label: 'Dine-In', icon: 'fa-cutlery' },
  { id: 'Birthday', label: 'Birthday', icon: 'fa-birthday-cake' },
  { id: 'Anniversary', label: 'Anniversary', icon: 'fa-heart' },
  { id: 'Party', label: 'Party', icon: 'fa-glass' },
];

const TableReservation: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user.user);

  const userTimeZone = useMemo(() => getUserTimeZone(), []);
  const timeZoneLabel = useMemo(() => getUserTimeZoneLabel(userTimeZone), [userTimeZone]);

  const [formData, setFormData] = useState<ReservationRequest>({
    fullName: user?.userName || '',
    phoneNumber: user?.phoneNumber || '',
    bookingDateTime: '',
    totalSeats: 2,
    occassion: 'Dine-In',
    timeZone: userTimeZone,
  });

  const [selectedOccasion, setSelectedOccasion] = useState<string>('Dine-In');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [featureBlocked, setFeatureBlocked] = useState<string | null>(null);
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [peppyOpen, setPeppyOpen] = useState(false);
  const [peppyEnabled, setPeppyEnabled] = useState(false);
  const [confirmedBookingWhen, setConfirmedBookingWhen] = useState('');
  const successBannerRef = useRef<HTMLDivElement>(null);
  const [bookingDateTimeLocal, setBookingDateTimeLocal] = useState(() =>
    utcIsoToDatetimeLocalValue(new Date().toISOString(), getUserTimeZone())
  );

  const resetBooking = useCallback(() => {
    setSuccess(false);
    setConfirmedBookingWhen('');
    setErrorMessage('');
    setPeppyOpen(false);
    setFormData({
      fullName: user?.userName || '',
      phoneNumber: user?.phoneNumber || '',
      bookingDateTime: '',
      totalSeats: 2,
      occassion: 'Dine-In',
      timeZone: userTimeZone,
    });
    setBookingDateTimeLocal(utcIsoToDatetimeLocalValue(new Date().toISOString(), userTimeZone));
    setSelectedOccasion('Dine-In');
  }, [user?.phoneNumber, user?.userName, userTimeZone]);

  useEffect(() => {
    if (!success) return;
    window.requestAnimationFrame(() => {
      successBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [success]);

  useEffect(() => {
    if (!appName) return;
    restaurantService.getRestaurant(appName).then((res) => {
      if (res.restaurant) {
        setRestaurantId(String(res.restaurant.id));
        setRestaurantName(res.restaurant.restaurantName || appName);
      }
      const features: PlanFeaturesPayload | undefined = res.features;
      if (!hasCustomerFeature(features, CUSTOMER_FEATURE_TABLE_BOOKING)) {
        setFeatureBlocked('Table booking is not available for this restaurant.');
      }
      setPeppyEnabled(hasCustomerFeature(features, CUSTOMER_FEATURE_VIRTUAL_CAPTAIN));
    });
  }, [appName]);

  useEffect(() => {
    if (!appName) return;
    restaurantService
      .getTableLocations(appName)
      .then((res) => {
        if (!res.success) return;
        const rows = (res.locations ?? []).filter(Boolean);
        setLocations(rows);
        if (rows.length === 1) {
          setSelectedLocationId(rows[0].id);
        } else if (rows.length > 1) {
          setSelectedLocationId(res.defaultLocationId ?? rows[0].id);
        } else {
          setSelectedLocationId(null);
        }
      })
      .catch(() => {
        // non-blocking
      });
  }, [appName]);

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^(?:(?:\+|0{0,2})91(\s*[\-]\s*)?|[0]?)?[6789]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'totalSeats' ? parseInt(value, 10) || 0 : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const id = val ? Number(val) : null;
    setSelectedLocationId(Number.isFinite(id as number) ? (id as number) : null);
  };

  const handleOccasionClick = (occasionId: string) => {
    setSelectedOccasion(occasionId);
    setFormData((prev) => ({
      ...prev,
      occassion: occasionId,
    }));
  };

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBookingDateTimeLocal(e.target.value);
    if (errors.bookingDateTime) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.bookingDateTime;
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Please enter your name';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Please enter your phone number';
    } else if (!validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!bookingDateTimeLocal.trim()) {
      newErrors.bookingDateTime = 'Please select booking date and time';
    } else {
      const iso = datetimeLocalValueToUtcIso(bookingDateTimeLocal, userTimeZone);
      const bookingDate = iso ? new Date(iso) : null;
      if (!bookingDate || Number.isNaN(bookingDate.getTime())) {
        newErrors.bookingDateTime = 'Please select a valid date and time';
      } else if (bookingDate < new Date()) {
        newErrors.bookingDateTime = 'Booking date and time must be in the future';
      }
    }

    if (!formData.totalSeats || formData.totalSeats < 1) {
      newErrors.totalSeats = 'Please enter number of guests (minimum 1)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm() || !appName) {
      if (!appName) setErrorMessage('Restaurant not found');
      return;
    }

    setLoading(true);

    try {
      const bookingIso = datetimeLocalValueToUtcIso(bookingDateTimeLocal, userTimeZone);
      const response = await reservationService.createReservation(appName, {
        ...formData,
        bookingDateTime: bookingIso,
        timeZone: userTimeZone,
        locationId: selectedLocationId ?? undefined,
      });

      if (response.success) {
        trackEvent('reserve_table', {
          restaurant_app: appName,
          party_size: formData.totalSeats,
        });
        const whenLabel =
          response.reservation?.bookingDateTime != null
            ? formatReservationDateTime(response.reservation.bookingDateTime, userTimeZone)
            : formatReservationDateTime(bookingIso, userTimeZone);
        setConfirmedBookingWhen(whenLabel);
        setSuccess(true);
      } else {
        setErrorMessage(response.error || 'Failed to create reservation. Please try again.');
      }
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setErrorMessage(
        message ||
          (error instanceof Error ? error.message : 'Failed to create reservation. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    ...(appName
      ? [{ label: restaurantName || 'Restaurant', path: `/restaurants/${appName}` }]
      : []),
    { label: 'Table Reservation', active: true },
  ];

  const showLocationSelect = useMemo(() => locations.length > 1, [locations.length]);

  return (
    <div className="table-reservation-page">
      <Header />
      <PageContainer className="table-reservation-container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="reservation-hero">
          <div className="reservation-hero-icon" aria-hidden="true">
            <IconCalendarSmall size={22} />
          </div>
          <div>
            <h1 className="reservation-title">Reserve a table</h1>
            <p className="reservation-subtitle">
              Share your details and we&apos;ll confirm your booking shortly.
            </p>
          </div>
        </div>

        {featureBlocked && (
          <div className="reservation-card reservation-card--blocked">
            <AuthMessage variant="error">{featureBlocked}</AuthMessage>
            <button
              type="button"
              className="reservation-btn reservation-btn--secondary"
              onClick={() => navigate(`/restaurants/${appName}`)}
            >
              Back to restaurant
            </button>
          </div>
        )}

        {!featureBlocked && (
          <div className="reservation-card">
            {success ? (
              <div ref={successBannerRef} className="reservation-success-panel">
                <AuthMessage variant="success">
                  Your table request is in! We&apos;ll notify you once the restaurant confirms.
                  {confirmedBookingWhen ? (
                    <>
                      {' '}
                      Requested for <strong>{confirmedBookingWhen}</strong>.
                    </>
                  ) : null}
                </AuthMessage>
                <div className="reservation-success-actions">
                  <button
                    type="button"
                    className="reservation-btn reservation-btn--primary"
                    onClick={() => navigate(`/restaurants/${appName}`)}
                  >
                    Back to restaurant
                  </button>
                  <button
                    type="button"
                    className="reservation-btn reservation-btn--secondary"
                    onClick={resetBooking}
                  >
                    Book another table
                  </button>
                </div>
              </div>
            ) : (
              <>
            {errorMessage && (
              <AuthMessage variant="error">{errorMessage}</AuthMessage>
            )}

              <div className={`reservation-layout${peppyOpen && peppyEnabled ? ' reservation-layout--peppy-open' : ''}`}>
                {peppyEnabled && appName && restaurantId && (
                  <div className="reservation-layout__peppy">
                    <div className="peppy-reservation-shell">
                      <div className="peppy-reservation-shell__header">
                        <div className="peppy-reservation-shell__title">
                          <strong>Peppy AI</strong>
                          <span>Book faster with chat</span>
                        </div>
                        <button
                          type="button"
                          className="peppy-reservation-shell__toggle"
                          onClick={() => setPeppyOpen((v) => !v)}
                          aria-expanded={peppyOpen}
                        >
                          {peppyOpen ? 'Collapse' : 'Open'}
                        </button>
                      </div>
                      {!peppyOpen ? (
                        <button
                          type="button"
                          className="peppy-reservation-shell__cta"
                          onClick={() => setPeppyOpen(true)}
                        >
                          Chat with Peppy AI to book
                        </button>
                      ) : (
                        <PeppyReservationAssistant
                          appName={appName}
                          restaurantId={restaurantId}
                          restaurantName={restaurantName || appName}
                          userId={user?.id ?? null}
                          guestName={formData.fullName || user?.userName}
                          guestPhone={formData.phoneNumber || user?.phoneNumber}
                          locations={locations.map((loc) => ({ id: loc.id, name: loc.name }))}
                          onConfirmed={(reservation) => {
                            setSuccess(true);
                            setErrorMessage('');
                            if (reservation?.bookingDateTime) {
                              setConfirmedBookingWhen(
                                formatReservationDateTime(reservation.bookingDateTime, userTimeZone)
                              );
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                <div className="reservation-layout__manual">
                  {peppyOpen && (
                    <>
                      <h2 className="reservation-layout__manual-title">Or book manually</h2>
                      <p className="reservation-layout__manual-hint">
                        Fill in the form if you prefer not to use chat.
                      </p>
                    </>
                  )}
                  <form className="reservation-form reservation-form--default" onSubmit={handleSubmit} noValidate>
                <div className="reservation-field">
                  <label htmlFor="fullName">Full name</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className={errors.fullName ? 'has-error' : ''}
                    autoComplete="name"
                  />
                  {errors.fullName && <span className="field-error">{errors.fullName}</span>}
                </div>

                <div className="reservation-field">
                  <label htmlFor="phoneNumber">Phone number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                    className={errors.phoneNumber ? 'has-error' : ''}
                    autoComplete="tel"
                  />
                  {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
                </div>

                <div className="reservation-field-row">
                  <div className="reservation-field">
                    <label htmlFor="bookingDateTime">
                      Date &amp; time <span className="reservation-field__tz">({timeZoneLabel})</span>
                    </label>
                    <input
                      type="datetime-local"
                      id="bookingDateTime"
                      value={bookingDateTimeLocal}
                      onChange={handleDateTimeChange}
                      min={utcIsoToDatetimeLocalValue(new Date().toISOString(), userTimeZone)}
                      className={errors.bookingDateTime ? 'has-error' : ''}
                    />
                    {errors.bookingDateTime && (
                      <span className="field-error">{errors.bookingDateTime}</span>
                    )}
                  </div>

                  <div className="reservation-field">
                    <label htmlFor="totalSeats">Guests</label>
                    <input
                      type="number"
                      id="totalSeats"
                      name="totalSeats"
                      value={formData.totalSeats}
                      onChange={handleInputChange}
                      min={1}
                      max={30}
                      className={errors.totalSeats ? 'has-error' : ''}
                    />
                    {errors.totalSeats && <span className="field-error">{errors.totalSeats}</span>}
                  </div>
                </div>

                {showLocationSelect && (
                  <div className="reservation-field">
                    <label htmlFor="locationId">Location preference</label>
                    <select
                      id="locationId"
                      name="locationId"
                      value={selectedLocationId ?? ''}
                      onChange={handleLocationChange}
                    >
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <fieldset className="reservation-occasions">
                  <legend>Occasion</legend>
                  <div className="occasion-list">
                    {occasions.map((occasion) => (
                      <button
                        key={occasion.id}
                        type="button"
                        className={`occasion-item ${selectedOccasion === occasion.id ? 'active' : ''}`}
                        onClick={() => handleOccasionClick(occasion.id)}
                        aria-pressed={selectedOccasion === occasion.id}
                      >
                        <i className={`fa ${occasion.icon}`} aria-hidden="true" />
                        <span>{occasion.label}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>

                <button type="submit" className="reservation-btn reservation-btn--primary" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Booking…
                    </>
                  ) : (
                    'Reserve now'
                  )}
                </button>
                  </form>
                </div>
              </div>
              </>
            )}
          </div>
        )}
      </PageContainer>

      <Footer />
    </div>
  );
};

export default TableReservation;
